/*
 * Access to the experimental Keyboard API (`navigator.keyboard`).
 *
 * Hotkeys are captured and stored by physical key position
 * (`KeyboardEvent.code`), whose names follow the US QWERTY layout:
 * "Semicolon" is the key right of L even when the active layout types "M"
 * there (French AZERTY). `getLayoutMap()` tells us which character the
 * active layout actually produces at each position, letting the UI display
 * the key as the user sees it on their keyboard.
 *
 * The API is Chromium-only (always present in Electron) and not part of the
 * TypeScript DOM library, hence the runtime validation instead of types.
 */

import { type Keymap } from "@/utils/keymap";
import { property } from "@/utils/unknown";

function isIterable(value: unknown): value is Iterable<unknown> {
  return typeof property(value, Symbol.iterator) === "function";
}

// Retrieve the active keyboard layout as a map from physical key codes
// (`KeyboardEvent.code`) to the characters the layout produces at these
// positions, or null when the Keyboard API is unavailable.
export async function getKeyboardLayout(): Promise<Keymap.Layout | null> {
  const keyboard = property(navigator, "keyboard");
  const getLayoutMap = property(keyboard, "getLayoutMap");
  if (typeof getLayoutMap !== "function") return null;
  try {
    // A `KeyboardLayoutMap` is a maplike whose default iterator yields
    // [code, character] entries.
    const layoutMap: unknown = await Reflect.apply(getLayoutMap, keyboard, []);
    if (!isIterable(layoutMap)) return null;
    const layout = new Map<string, string>();
    for (const entry of layoutMap) {
      if (!Array.isArray(entry)) continue;
      const [code, character] = entry;
      if (typeof code === "string" && typeof character === "string") {
        layout.set(code, character);
      }
    }
    return layout;
  } catch {
    return null;
  }
}
